import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  Home,
  LogOut,
  MessageCircle,
  Store,
  type LucideIcon,
} from "lucide-react";
import { Suspense, type ReactNode } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";

import { inboxApi } from "@/api/endpoints/inbox";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/stores/authStore";

import { useStokkieTitle } from "../stokkie/useStokkieTitle";
import { StokkieLogo } from "./StokkieLogo";
/**
 * The stokvel portal's frame.
 *
 * Its own layout rather than the back office's shell: the people this is for
 * are on entry-level phones, often on prepaid data, and many do not use apps
 * much. So: the logo and a way out at the top, and four big labelled buttons at
 * the bottom within reach of a thumb — Home, Market, Messages, Alerts — the
 * only places anybody ever needs to find. Everything else is reached from Home.
 *
 * A shop owner reaches it from the back office with the same account — one
 * identity, two portals — and the header offers the way back.
 */
export function StokvelLayout() {
  useStokkieTitle();
  const session = useAuthStore((s) => s.session);
  const logout = useAuthStore((s) => s.logout);
  const inbox = useQuery({
    queryKey: ["inbox"],
    queryFn: () => inboxApi.get(),
    // Every two minutes, and whenever the app comes back into view: often
    // enough to see a join request while the screen is open, rarely enough not
    // to eat a prepaid bundle on a phone left on the counter.
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-[1.0625rem]">
      <header className="sticky top-0 z-20 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-3xl items-center gap-3 px-4">
          <Link to="/stokvel" aria-label="Home">
            <StokkieLogo className="h-11" />
          </Link>
          {session?.user.isDemo && (
            <span className="rounded-full bg-gold-50 px-2.5 py-0.5 text-xs font-bold text-gold-700">
              Demo
            </span>
          )}
          <span className="ml-auto" />
          {session?.merchant && (
            <Link
              to="/"
              className="inline-flex h-10 items-center rounded-full bg-canvas px-3 text-sm font-semibold text-brand-600"
            >
              My shop
            </Link>
          )}
          <button
            type="button"
            onClick={() => void logout()}
            className="inline-flex h-10 items-center gap-1.5 rounded-full bg-canvas px-3 text-sm font-semibold text-ink-600"
          >
            <LogOut className="size-4" aria-hidden />
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-28 pt-5">
        <Suspense
          fallback={<p className="py-8 text-center text-ink-500">Loading…</p>}
        >
          <Outlet />
        </Suspense>
      </main>

      <nav
        aria-label="Stokkie Stokvel"
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgb(0_0_0/0.06)]"
      >
        <div className="mx-auto flex h-[4.5rem] max-w-3xl">
          <BottomLink to="/stokvel" end icon={Home}>
            Home
          </BottomLink>
          <BottomLink to="/stokvel/market" icon={Store}>
            Market
          </BottomLink>
          <BottomLink to="/stokvel/messages" icon={MessageCircle}>
            Messages
          </BottomLink>
          <BottomLink
            to="/stokvel/inbox"
            icon={Bell}
            badge={inbox.data?.unread ?? 0}
          >
            Alerts
          </BottomLink>
        </div>
      </nav>
    </div>
  );
}

function BottomLink({
  to,
  end,
  icon: Icon,
  badge = 0,
  children,
}: {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  badge?: number;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          "relative flex flex-1 flex-col items-center justify-center gap-1 text-sm font-semibold",
          isActive ? "text-brand-600" : "text-ink-500",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              "grid h-8 w-14 place-items-center rounded-full",
              isActive && "bg-brand-50",
            )}
          >
            <Icon className="size-6" aria-hidden />
          </span>
          {children}
          {badge > 0 && (
            <span className="absolute right-[calc(50%-1.75rem)] top-1.5 grid min-w-5 place-items-center rounded-full bg-danger-500 px-1 text-xs font-bold text-white">
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * Signed in, or sent to the cellphone sign-in — not the till's login screen,
 * which asks for a PIN pad or an email address a member does not have. Where
 * they were going travels along, so an invite link still lands on its group.
 */
export function RequireStokvelSession({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const session = useAuthStore((s) => s.session);
  const location = useLocation();

  if (status === "loading") return null;
  if (status === "anonymous") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/stokvel/start?next=${next}`} replace />;
  }
  // SmartSpaza staff and wholesalers do not join stokvels (§3); the server
  // refuses them too.
  if (session && !session.user.isPersonal && !session.merchant) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
